import {useDeviceListQuery, useDeviceRestartFirmwareUpdateMutation, useDevicesCommandMutation} from "../../app/api/api";
import {
    CellContent,
    ColumnDef,
    FilterDef,
    FilterOperator,
    FilterType,
    ListRequestIdModelTable,
} from "../../components/ExTable";

import {useTranslation} from "react-i18next";
import {formatTimestamp} from "../../utils/datetime";
import {Box, Chip, IconButton, Tooltip} from "@mui/material";
import {
    AddCircleOutline as CreateIcon,
    AddLink as LinkIcon,
    Cached as CachedIcon,
    HourglassEmpty as WaitingForCheckinIcon,
    LinkOff as UnlinkIcon,
    MyLocation as MyLocationIcon,
    SecurityUpdate as UpdateOngoing,
    SecurityUpdateGood as UpToDateIcon,
    SecurityUpdateWarning as UpdateFailed,
    Update as UpdateScheduledIcon,
} from "@mui/icons-material/";
import {ReactComponent as RestartUpdateIcon} from "../../assets/icons/reset.svg";
import React, {useState} from "react";
import DeviceFwUpdateDialog from "./components/DeviceFirmwareUpdateDialog";
import _, {capitalize, lowerCase} from "lodash";
import {DeviceField, DeviceFlat, FirmwareUpdateStatus, PlSentStatus} from "../../app/api/model/device";
import {DeviceCommand} from "../../app/api/model/deviceHistory";
import {ordinaryDetailPageLinkTo} from "../../utils/ordinaryDetailPageLink";
import Page from "../../components/Layout/Page";
import {getTranslationKey, TranslationContext} from "../../utils/getTranslationKey";
import {RoutePath} from "../../types";
import dayjs from "dayjs";
import {QuestionMark, UploadFile} from "@mui/icons-material";
import DeviceImportCsvDialog from "./DeviceImportCsvDialog";
import AddEditDeviceDialog from "./components/AddEditDeviceDialog";
import {useModal} from "../../hooks/useModal";
import {ResponseErrorAlert} from "../../components/ResponseErrorMessages";
import {useSnackbar} from "notistack";
import DeviceAssignToOrganizationDialog, {
    unassignedOrSameOrganization,
} from "./components/DeviceAssignToOrganizationDialog";
import DeviceRemoveFromOrganizationDialog from "./components/DeviceRemoveFromOrganizationDialog";
import {usePermission} from "../../hooks/usePermission";
import {PermissionLevel} from "../../app/api/model/user";
import UpdateIcon from "@mui/icons-material/Update";
import CancelFirmwareUpdateButton from "./components/CancelFirmwareUpdateButton";
import ClearIcon from "@mui/icons-material/Clear";
import DeviceCapabilitiesCell from "./components/DeviceCapabilitiesCell";

export default function DeviceList() {
    const {t} = useTranslation();
    const createDeviceModal = useModal(false);
    const fwUpdateDialogModal = useModal(false);
    const deviceDialogModal = useModal(false);
    const assignToOrgModal = useModal(false);
    const removeFromOrgModal = useModal(false);
    const [selected, setSelected] = useState<DeviceFlat[]>();
    const [devicesCommand] = useDevicesCommandMutation();
    const {enqueueSnackbar} = useSnackbar();
    const {hasPermission, hasExactPermission} = usePermission();
    const [deviceFirmwareUpdate] = useDeviceRestartFirmwareUpdateMutation();
    const formatFirmwareUpdateStatus = ({row}: {row: DeviceFlat}) => {
        const firmwareUpdateStatus = row[DeviceField.FIRMWARE_UPDATE_STATUS];

        const columnContent: CellContent = {
            title: undefined,
            node: <QuestionMark color="error" />,
        };
        const scheduled =
            firmwareUpdateStatus === FirmwareUpdateStatus.SCHEDULED ||
            (firmwareUpdateStatus === FirmwareUpdateStatus.IN_PROGRESS &&
                row[DeviceField.FIRMWARE_UPDATE_PROGRESS] < 1 &&
                !row[DeviceField.ONLINE]);
        const titleParts = [
            t(
                getTranslationKey({
                    context: TranslationContext.FIRMWARE_UPDATE_STATUS,
                    value: scheduled ? (1).toFixed() : (firmwareUpdateStatus || 0).toFixed(),
                })
            ) +
                (scheduled
                    ? ` - ${
                          dayjs.utc(row[DeviceField.FIRMWARE_UPDATE_SCHEDULED_AT]).isAfter(dayjs())
                              ? formatTimestamp(row[DeviceField.FIRMWARE_UPDATE_SCHEDULED_AT])
                              : t("generic_asap")
                      }`
                    : ""),
        ];
        switch (firmwareUpdateStatus) {
            case FirmwareUpdateStatus.UP_TO_DATE:
                columnContent.node = <UpToDateIcon color="success" />;
                break;

            case FirmwareUpdateStatus.WAITING_FOR_CHECKIN:
                columnContent.node = <WaitingForCheckinIcon color="info" />;
                break;

            case FirmwareUpdateStatus.SCHEDULED:
                columnContent.node = (
                    <UpdateScheduledIcon
                        color={
                            dayjs(row[DeviceField.FIRMWARE_UPDATE_SCHEDULED_AT]).isAfter(dayjs())
                                ? "disabled"
                                : "warning"
                        }
                    />
                );
                break;
            case FirmwareUpdateStatus.IN_PROGRESS:
                if (!row[DeviceField.ONLINE] && row[DeviceField.FIRMWARE_UPDATE_PROGRESS] === 0) {
                    if (
                        (!row[DeviceField.LAST_CONNECTION_AT] ||
                            dayjs(row[DeviceField.LAST_CONNECTION_AT]).add(3, "day").isBefore(dayjs())) &&
                        row[DeviceField.FIRMWARE_UPDATE_SCHEDULED_AT] &&
                        dayjs(row[DeviceField.FIRMWARE_UPDATE_SCHEDULED_AT]).add(3, "day").isBefore(dayjs())
                    ) {
                        columnContent.node = <UpdateScheduledIcon color="error" />;
                        titleParts.push(t("device_offlineForLong"));
                        break;
                    }
                    columnContent.node = <UpdateScheduledIcon color="info" />;
                    break;
                } else if (row[DeviceField.FIRMWARE_UPDATE_PROGRESS] >= 99) {
                    columnContent.node = <UpToDateIcon color="info" />;
                    titleParts.push(t("device_rebooting"));
                    break;
                }
                columnContent.node = (
                    <Box display={"flex"} justifyContent={"center"}>
                        <UpdateOngoing color="info" />
                        <Chip
                            label={`${row[DeviceField.FIRMWARE_UPDATE_PROGRESS]}%`}
                            color="primary"
                            variant="outlined"
                            size={"small"}
                        />
                    </Box>
                );
                break;
            case FirmwareUpdateStatus.ERROR:
                columnContent.node = <UpdateFailed color="error" />;
                break;
            case FirmwareUpdateStatus.UNKNOWN:
                columnContent.node = <QuestionMark color="warning" />;
                break;
            case FirmwareUpdateStatus.CANCELED:
                columnContent.node = <ClearIcon color="warning" />;
                break;
            case FirmwareUpdateStatus.WAITING_FOR_RETRY:
                columnContent.node = <UpdateFailed color="warning" />;
                break;
        }
        if (firmwareUpdateStatus === FirmwareUpdateStatus.UNKNOWN) {
            titleParts.push(t(row[DeviceField.FIRMWARE_UPDATE_ERROR]));
        } else if (
            firmwareUpdateStatus !== FirmwareUpdateStatus.UP_TO_DATE &&
            firmwareUpdateStatus !== FirmwareUpdateStatus.WAITING_FOR_CHECKIN
        ) {
            titleParts.push(
                `${t("device_targetFirmwareVersion")}: ${row[DeviceField.TARGET_FIRMWARE_VERSION_NUMBER]} ${
                    row[DeviceField.TARGET_FIRMWARE_LEGACY] ? `(${t("generic_legacy")})` : ""
                }`
            );

            if (row[DeviceField.FIRMWARE_UPDATE_TRY_COUNT] > 1) {
                titleParts.push(`${row[DeviceField.FIRMWARE_UPDATE_TRY_COUNT]}. ${t("device_firmwareUpdateAttempt")}`);
            }
        }

        if (firmwareUpdateStatus === FirmwareUpdateStatus.ERROR) {
            titleParts.push(t(row[DeviceField.FIRMWARE_UPDATE_ERROR]));
        }
        columnContent.title = titleParts.join("\n");
        return columnContent;
    };

    const handleReset = async () => {
        const deviceIds: Array<number> = selected?.map((device) => device.id) || [];
        if (deviceIds.length) {
            try {
                await devicesCommand({deviceIds: deviceIds, command: DeviceCommand.RESET}).unwrap();
                enqueueSnackbar(t("device_smsCommandRequestSend"), {variant: "success"});
            } catch (error) {
                enqueueSnackbar(
                    <ResponseErrorAlert
                        title={t("generic_errorResponseTitle")}
                        body={t("generic_errorResponseBody")}
                    />,
                    {
                        variant: "error",
                    }
                );
            }
        }
    };

    const handleRequestLocation = async () => {
        const deviceIds: Array<number> = selected?.map((device) => device.id) || [];
        if (deviceIds.length) {
            try {
                await devicesCommand({deviceIds: deviceIds, command: DeviceCommand.REQUEST_LOCATION}).unwrap();
                enqueueSnackbar(t("device_smsCommandRequestSend"), {variant: "success"});
            } catch (error) {
                enqueueSnackbar(
                    <ResponseErrorAlert
                        title={t("generic_errorResponseTitle")}
                        body={t("generic_errorResponseBody")}
                    />,
                    {
                        variant: "error",
                    }
                );
            }
        }
    };

    const handleRestartFirmwareUpdate = async () => {
        const deviceIds: Array<number> = selected?.map((device) => device.id) || [];
        if (deviceIds.length) {
            try {
                await deviceFirmwareUpdate({deviceIds: deviceIds}).unwrap();
            } catch (error) {
                enqueueSnackbar(
                    <ResponseErrorAlert
                        title={t("generic_errorResponseTitle")}
                        body={t("generic_errorResponseBody")}
                    />,
                    {
                        variant: "error",
                    }
                );
            }
        }
    };

    const columnDefs: ColumnDef<DeviceFlat>[] = [
        {
            fieldName: DeviceField.ID,
            align: "left",
            label: t("generic_id"),
            width: 100,
            sortable: true,
            generateLink: ordinaryDetailPageLinkTo(RoutePath.DEVICES),
            sticky: true,
        },
        {
            fieldName: DeviceField.LEO_ID,
            align: "left",
            label: t("generic_leoId"),
            sortable: true,
            width: 250,
        },
        {
            fieldName: DeviceField.ICCID,
            align: "left",
            label: t("generic_iccid"),
            width: 200,
            sortable: true,
        },
        {
            fieldName: DeviceField.IMEI,
            align: "left",
            label: t("device_imei"),
            width: 175,
            sortable: true,
        },
        {
            fieldName: DeviceField.MSISDN,
            align: "left",
            label: t("device_msisdn"),
            width: 175,
            sortable: true,
        },
        {
            fieldName: DeviceField.LICENSE_PLATE,
            align: "left",
            label: t("device_licensePlate"),
            width: 150,
            sortable: true,
        },
        {
            fieldName: DeviceField.ORGANIZATION_NAME,
            align: "left",
            label: t("organizations_name"),
            width: 200,
            sortable: true,
        },
        {
            fieldName: DeviceField.ORGANIZATION_CUSTOMER_NUMBER,
            align: "left",
            label: t("organizations_mainDKVCustomerNumber"),
            width: 200,
            sortable: true,
        },
        {
            fieldName: DeviceField.TENANT,
            align: "left",
            label: t("device_tenant"),
            width: 150,
            sortable: true,
        },
        {
            fieldName: DeviceField.ONLINE,
            align: "center",
            label: t("device_online"),
            width: 80,
            sortable: true,
            formatCellText: ({row}) => {
                return {
                    title: `${t("device_lastConnectionAt")}: ${row[DeviceField.LAST_CONNECTION_AT] || "-"}`,
                    node: row[DeviceField.ONLINE] ? (
                        <Chip label="On" color="success" size="small" />
                    ) : (
                        <Chip label="Off" color="error" variant="outlined" size="small" />
                    ),
                };
            },
        },
        {
            fieldName: DeviceField.HARDWARE_TYPE,
            align: "left",
            label: t("device_hardwareType"),
            width: 100,
            sortable: true,
        },
        {
            fieldName: DeviceField.FIRMWARE_UPDATE_STATUS,
            align: "center",
            label: t("device_firmwareUpdateStatus"),
            width: 75,
            sortable: true,
            formatCellText: formatFirmwareUpdateStatus,
        },
        {
            fieldName: DeviceField.CURRENT_FIRMWARE_VERSION_NUMBER,
            align: "left",
            label: t("device_currentFirmwareVersion"),
            width: 200,
            sortable: true,
        },
        {
            fieldName: DeviceField.TARGET_FIRMWARE_VERSION_NUMBER,
            align: "left",
            label: t("device_targetFirmwareVersion"),
            width: 200,
            sortable: true,
        },
        {
            fieldName: DeviceField.CURRENT_FIRMWARE_LEGACY,
            align: "left",
            label: t("device_currentFirmwareLegacy"),
            width: 100,
            sortable: true,
            formatCellText: ({row}) => {
                return row[DeviceField.CURRENT_FIRMWARE_LEGACY] ? t("generic_yes") : t("generic_no");
            },
        },
        {
            fieldName: DeviceField.CAPABILITIES,
            align: "left",
            label: t("device_capabilities"),
            width: 200,
            sortable: false,
            formatCellText: ({row}) => ({
                node: <DeviceCapabilitiesCell deviceId={row[DeviceField.ID]} />,
            }),
        },
        {
            fieldName: DeviceField.PL_SENT_ID,
            align: "left",
            label: t("label_plSentId"),
            width: 150,
            sortable: true,
        },
        {
            fieldName: DeviceField.LAST_CONNECTION_AT,
            align: "left",
            label: t("device_lastConnectionAt"),
            width: 200,
            sortable: true,
            formatCellText: ({cellValue}) => {
                return formatTimestamp(cellValue);
            },
        },
        {
            fieldName: DeviceField.CREATED_AT,
            align: "left",
            label: t("generic_createdAt"),
            width: 150,
            sortable: true,
            formatCellText: ({cellValue}) => {
                return formatTimestamp(cellValue);
            },
        },
        {
            fieldName: DeviceField.UPDATED_AT,
            align: "left",
            label: t("generic_updatedAt"),
            width: 150,
            sortable: true,
            formatCellText: ({cellValue}) => {
                return formatTimestamp(cellValue);
            },
        },
    ];
    const filterDefs: FilterDef<DeviceFlat>[] = [
        {
            fieldName: DeviceField.MSISDN,
            filterOperators: [FilterOperator.IN, FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: DeviceField.ID,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
        },
        {
            fieldName: DeviceField.LEO_ID,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ, FilterOperator.IN],
        },
        {
            fieldName: DeviceField.ICCID,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ, FilterOperator.IN],
        },
        {
            fieldName: DeviceField.IMEI,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ, FilterOperator.IN],
        },
        {
            fieldName: DeviceField.ONLINE,
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            type: FilterType.BOOLEAN,
        },
        {
            fieldName: DeviceField.LICENSE_PLATE,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            type: FilterType.ENUM,
            fieldName: DeviceField.FIRMWARE_UPDATE_STATUS,
            filterOperators: [FilterOperator.EQ],
            label: t("device_firmwareUpdateStatus"),
            enumValues: Object.keys(FirmwareUpdateStatus)
                .filter(isIntegerString)
                .map((status) => {
                    const str = FirmwareUpdateStatus[parseInt(status)];
                    return {
                        value: status,
                        labelKey: `firmwareUpdateStatus_${toCamelCase(str)}`,
                    };
                }),
        },
        {
            fieldName: DeviceField.LAST_CONNECTION_AT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
        {
            fieldName: DeviceField.CURRENT_FIRMWARE_LEGACY,
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            type: FilterType.ENUM,
            enumValues: [
                {value: "true", labelKey: t("generic_yes")},
                {value: "false", labelKey: t("generic_no")},
            ],
        },
        {
            fieldName: DeviceField.CURRENT_FIRMWARE_VERSION_NUMBER,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: DeviceField.TARGET_FIRMWARE_VERSION_NUMBER,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: DeviceField.ORGANIZATION_CUSTOMER_NUMBER,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: DeviceField.ORGANIZATION_NAME,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: DeviceField.TENANT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.NUMBER,
        },
        {
            fieldName: DeviceField.PL_SENT_ID,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: DeviceField.PL_SENT_STATUS,
            type: FilterType.ENUM,
            label: t("label_plSentStatus"),
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            enumValues: Object.keys(PlSentStatus).map((status) => {
                return {
                    value: status,
                    labelKey: `plSent_status${capitalize(lowerCase(status))}`,
                };
            }),
        },
        {
            fieldName: DeviceField.HARDWARE_TYPE,
            filterOperators: [FilterOperator.CONTAINS, FilterOperator.STARTSWITH, FilterOperator.EQ],
        },
        {
            fieldName: DeviceField.CREATED_AT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
        {
            fieldName: DeviceField.UPDATED_AT,
            filterOperators: [
                FilterOperator.EQ,
                FilterOperator.LT,
                FilterOperator.GT,
                FilterOperator.LTE,
                FilterOperator.GTE,
            ],
            type: FilterType.DATE,
        },
        {
            fieldName: DeviceField.CAPABILITIES,
            label: t("device_capabilities"),
            type: FilterType.ENUM,
            filterOperators: [FilterOperator.CONTAINS],
            enumValues: [
                {value: "FMS", labelKey: "fmsState_capable"},
                {value: "DTCO", labelKey: "dtcoState_capable"},
                {value: "RDL", labelKey: "rdlState_capable"},
            ],
        },
    ];

    const renderWriteAbleActions = (entries: DeviceFlat[]) => {
        //check if at least one device is currently running an update
        return (
            <>
                {entries.length > 0 && (
                    <>
                        <Tooltip title={t("action_requestLocation")}>
                            <IconButton onClick={handleRequestLocation}>
                                <MyLocationIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={t("action_rebootDevice")}>
                            <IconButton onClick={handleReset}>
                                <CachedIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={t("action_updateFirmware")}>
                            <IconButton
                                disabled={
                                    fwUpdateDialogModal.isOpen ||
                                    _.uniq(entries.map((x) => x[DeviceField.HARDWARE_TYPE])).length > 1
                                }
                                onClick={fwUpdateDialogModal.toggleIsOpen}
                            >
                                <UpdateIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t("action_restartFirmwareUpdate")}>
                            <IconButton onClick={handleRestartFirmwareUpdate}>
                                <RestartUpdateIcon />
                            </IconButton>
                        </Tooltip>

                        <CancelFirmwareUpdateButton devices={entries} />

                        <Tooltip
                            title={
                                unassignedOrSameOrganization(entries)
                                    ? t("action_assignDeviceToOrg")
                                    : t("device_selectionNotOfSameOrganization")
                            }
                        >
                            <span>
                                <IconButton
                                    disabled={assignToOrgModal.isOpen || !unassignedOrSameOrganization(entries)}
                                    onClick={assignToOrgModal.toggleIsOpen}
                                >
                                    <LinkIcon />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip
                            title={
                                unassignedOrSameOrganization(entries)
                                    ? t("action_removeDeviceFromOrg")
                                    : t("device_selectionNotOfSameOrganization")
                            }
                        >
                            <span>
                                <IconButton
                                    disabled={removeFromOrgModal.isOpen || !unassignedOrSameOrganization(entries)}
                                    onClick={removeFromOrgModal.toggleIsOpen}
                                >
                                    <UnlinkIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </>
                )}

                <Tooltip title={t("action_createDeviceViaCsv")}>
                    <IconButton onClick={deviceDialogModal.toggleIsOpen}>
                        <UploadFile />
                    </IconButton>
                </Tooltip>

                <Tooltip title={t("action_createDevice")}>
                    <IconButton onClick={createDeviceModal.toggleIsOpen}>
                        <CreateIcon />
                    </IconButton>
                </Tooltip>
            </>
        );
    };

    const renderSupportActions = (entries: DeviceFlat[]) => {
        //check if at least one device is currently running an update
        return (
            <>
                {entries.length > 0 && (
                    <>
                        <Tooltip title={t("action_requestLocation")}>
                            <IconButton onClick={handleRequestLocation}>
                                <MyLocationIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={t("action_rebootDevice")}>
                            <IconButton onClick={handleReset}>
                                <CachedIcon />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={t("action_restartFirmwareUpdate")}>
                            <IconButton onClick={handleRestartFirmwareUpdate}>
                                <RestartUpdateIcon />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </>
        );
    };

    function getToolbarActionsForPermission() {
        if (hasExactPermission(PermissionLevel.VIEW_ONLY)) {
            return undefined;
        } else if (hasExactPermission(PermissionLevel.SUPPORT)) {
            return renderSupportActions;
        }

        return hasPermission(PermissionLevel.MANAGER) ? renderWriteAbleActions : undefined;
    }

    return (
        <Page>
            <ListRequestIdModelTable
                dataTest="devicesList"
                queryHook={useDeviceListQuery}
                columnDefs={columnDefs}
                filterDefs={filterDefs}
                title={t("generic_devices")}
                renderActions={getToolbarActionsForPermission()}
                onSelectedChange={setSelected}
                pollingInterval={5000}
            />
            <AddEditDeviceDialog modal={createDeviceModal} />
            <DeviceFwUpdateDialog
                open={fwUpdateDialogModal.isOpen}
                onClose={fwUpdateDialogModal.toggleIsOpen}
                deviceIds={selected?.map((d) => d[DeviceField.ID])}
                hardwareType={selected?.[0]?.[DeviceField.HARDWARE_TYPE]}
                defaultFirmwareId={selected?.[0]?.[DeviceField.CURRENT_FIRMWARE_ID]}
            />
            <DeviceImportCsvDialog open={deviceDialogModal.isOpen} onClose={deviceDialogModal.toggleIsOpen} />
            <DeviceAssignToOrganizationDialog
                open={assignToOrgModal.isOpen}
                onClose={assignToOrgModal.toggleIsOpen}
                devices={selected}
            />
            <DeviceRemoveFromOrganizationDialog
                open={removeFromOrgModal.isOpen}
                onClose={removeFromOrgModal.toggleIsOpen}
                devices={selected}
            />
        </Page>
    );
}

function isIntegerString(str: string): boolean {
    return /^-?\d+$/.test(str);
}

// turn constant to camel case, e.g. ABC_DEF_GHI to abcDefGhi
function toCamelCase(str: string): string {
    return str.toLowerCase().replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}
